import { useMemo } from "react"
import type { ApiCustomer, ApiEstimate, EstimateStatus } from "@/lib/api/types"
import { useEstimates } from "@/lib/api/use-estimates"
import { useOrganization } from "@/lib/organization/organization-context"

// Assembles the whole-customer view Sam asked for (Jul 10: "look up the
// customer, click the estimate, see their communications") from data we
// already have. There is no top-level customer endpoint — a customer is
// carried on estimate.vehicle.customer — so we aggregate the team's
// estimates by customer.id into one profile: every estimate they've had
// (grouped by vehicle), the opportunity we found for them, and a status
// breakdown. The automations/conversation half is layered on top from the
// (mock) customer-keyed marketing feed; this hook owns identity + history.
//
// Spans the active dealership AND body shop (like useVinDuplicates), so a
// customer seen at both the service drive and the body shop reads as one
// person — the hand-off, not two disconnected records.
//
// Dev note (main re-impl): the durable identity is a backend Customer /
// GHL-contact entity keyed across the org group; grouping by customer.id
// client-side is the design-branch stand-in.

const OPEN_STATUSES: EstimateStatus[] = [
  "Draft",
  "Loading",
  "Pending",
  "Confirmed",
  "InProgress",
  "Scheduled",
]

export interface CustomerVehicleGroup {
  vin: string | null
  label: string
  estimates: ApiEstimate[]
}

export interface CustomerProfile {
  customer: ApiCustomer | null
  /** All of this customer's estimates, newest first. */
  estimates: ApiEstimate[]
  /** Estimates grouped by vehicle, each group newest-first. */
  vehicles: CustomerVehicleGroup[]
  /** Σ avg across estimates — the opportunity we've identified for them. */
  totalIdentified: number
  counts: { total: number; open: number; converted: number; declined: number }
  /** Newest updatedAt/createdAt across their estimates, or null. */
  lastActivityAt: string | null
}

const EMPTY: CustomerProfile = {
  customer: null,
  estimates: [],
  vehicles: [],
  totalIdentified: 0,
  counts: { total: 0, open: 0, converted: 0, declined: 0 },
  lastActivityAt: null,
}

function vehicleLabel(e: ApiEstimate): string {
  return (
    [e.vehicle?.year, e.vehicle?.make, e.vehicle?.model]
      .filter(Boolean)
      .join(" ") || "Vehicle"
  )
}

export function useCustomerProfile(
  customerId: number | string | null,
): CustomerProfile & { isLoading: boolean } {
  const { activeDealershipId, activeBodyShopId } = useOrganization()
  const dealership = useEstimates(activeDealershipId ?? null)
  const bodyShop = useEstimates(activeBodyShopId ?? null)
  const isLoading = dealership.isLoading || bodyShop.isLoading

  const profile = useMemo(() => {
    const idStr = customerId != null ? String(customerId) : null
    if (!idStr) return EMPTY

    // Merge both teams, dedupe by estimate id, keep only this customer's
    // active estimates.
    const byId = new Map<string, ApiEstimate>()
    for (const e of [...(dealership.data ?? []), ...(bodyShop.data ?? [])]) {
      if (e.isActive === false) continue
      if (String(e.vehicle?.customer?.id ?? "") !== idStr) continue
      byId.set(String(e.id), e)
    }

    const estimates = [...byId.values()].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )
    if (estimates.length === 0) return EMPTY

    const customer = estimates[0].vehicle?.customer ?? null

    // Group by VIN (fall back to a per-estimate key when the VIN is absent).
    const vmap = new Map<string, ApiEstimate[]>()
    for (const e of estimates) {
      const vin = e.vehicle?.vin?.toUpperCase() || `__novin_${e.id}`
      const arr = vmap.get(vin) ?? []
      arr.push(e)
      vmap.set(vin, arr)
    }
    const vehicles: CustomerVehicleGroup[] = [...vmap.entries()].map(
      ([vin, list]) => ({
        vin: vin.startsWith("__novin_") ? null : vin,
        label: vehicleLabel(list[0]),
        estimates: list,
      }),
    )

    const counts = {
      total: estimates.length,
      open: estimates.filter((e) => OPEN_STATUSES.includes(e.status)).length,
      converted: estimates.filter((e) => e.status === "Completed").length,
      declined: estimates.filter((e) => e.status === "Declined").length,
    }
    const totalIdentified = estimates.reduce((s, e) => s + (e.avg || 0), 0)
    const lastActivityAt = estimates.reduce<string | null>((latest, e) => {
      const t = e.updatedAt || e.createdAt
      return !latest || new Date(t) > new Date(latest) ? t : latest
    }, null)

    return { customer, estimates, vehicles, totalIdentified, counts, lastActivityAt }
  }, [customerId, dealership.data, bodyShop.data])

  return { ...profile, isLoading }
}

// Where a customer stands with the shop — ONE state per person, derived from
// their estimate statuses exactly the way the follow-up engine derives its
// focus estimate (nothing invented). Priority: the action ('confirm') wins,
// then data quality ('no_contact'), then the newest post-confirm outcome.
export type CustomerFollowUpState =
  | "confirm" // a fresh estimate is waiting on the human OK
  | "no_contact" // no phone AND no email — follow-up can't run
  | "to_call" // reviewed and approved, but nobody has contacted them yet
  | "following" // contact has started — the drip is live (InProgress)
  | "booked" // Scheduled
  | "won" // Completed
  | "passed" // Declined (or nothing actionable)

export interface CustomerSummary {
  id: number
  name: string
  phone: string | null
  email: string | null
  estimateCount: number
  totalIdentified: number
  lastActivityAt: string
  followUp: CustomerFollowUpState
  /** Distinct vehicle labels, newest first. */
  vehicles: string[]
}

// Pending/Loading only — a Draft is the writer's work-in-progress, not an
// estimate anyone can confirm, so it must not brand a customer "to confirm".
const UNCONFIRMED: EstimateStatus[] = ["Loading", "Pending"]

function deriveFollowUpState(
  c: ApiCustomer,
  sorted: ApiEstimate[],
): CustomerFollowUpState {
  if (sorted.some((e) => UNCONFIRMED.includes(e.status))) return "confirm"
  if (!c.phone && !c.email) return "no_contact"
  const focus = sorted.find((e) =>
    ["Confirmed", "InProgress", "Scheduled", "Completed", "Declined"].includes(e.status),
  )
  switch (focus?.status) {
    case "Scheduled":
      return "booked"
    case "Completed":
      return "won"
    // Confirmed no longer means "the drip is running" — it means a human read
    // the estimate and nobody has reached out yet. Folding it into "following"
    // would tell the shop this customer is being worked when they are not.
    case "Confirmed":
      return "to_call"
    case "InProgress":
      return "following"
    default:
      return "passed"
  }
}

// The customer directory — one row per person across both stores, newest
// activity first. Powers the "look up the customer" front door (Sam, Jul 10).
export function useCustomerDirectory(): {
  customers: CustomerSummary[]
  isLoading: boolean
} {
  const { activeDealershipId, activeBodyShopId } = useOrganization()
  const dealership = useEstimates(activeDealershipId ?? null)
  const bodyShop = useEstimates(activeBodyShopId ?? null)
  const isLoading = dealership.isLoading || bodyShop.isLoading

  const customers = useMemo(() => {
    const byCustomer = new Map<number, { c: ApiCustomer; ests: ApiEstimate[] }>()
    const seenEstimate = new Set<string>()
    for (const e of [...(dealership.data ?? []), ...(bodyShop.data ?? [])]) {
      if (e.isActive === false) continue
      // Drafts are unsent write-ups — nothing has been "found for" the
      // customer yet, so they don't inflate the book's totals or rows (#61).
      if (e.status === "Draft") continue
      if (seenEstimate.has(String(e.id))) continue
      seenEstimate.add(String(e.id))
      const c = e.vehicle?.customer
      if (!c?.id) continue
      const entry = byCustomer.get(c.id) ?? { c, ests: [] }
      entry.ests.push(e)
      byCustomer.set(c.id, entry)
    }
    const list: CustomerSummary[] = [...byCustomer.values()].map(({ c, ests }) => {
      const sorted = [...ests].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )
      const vehicles = [...new Set(sorted.map((e) => vehicleLabel(e)))]
      return {
        id: c.id,
        name: c.name,
        phone: c.phone,
        email: c.email,
        estimateCount: ests.length,
        totalIdentified: ests.reduce((s, e) => s + (e.avg || 0), 0),
        lastActivityAt: sorted[0].updatedAt || sorted[0].createdAt,
        followUp: deriveFollowUpState(c, sorted),
        vehicles,
      }
    })
    return list.sort(
      (a, b) =>
        new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime(),
    )
  }, [dealership.data, bodyShop.data])

  return { customers, isLoading }
}
